import { useCallback, useEffect, useState } from 'react';
import { JobListItem } from '../jobs.model';
import { JobStatus } from '../constant';

interface UseJobSelectionProps {
  jobsCount: number;
}

export const useJobSelection = ({ jobsCount }: UseJobSelectionProps) => {
  const [selectedJobs, setSelectedJobs] = useState<JobListItem[]>([]);
  const [isSelectAllAcrossPagination, setIsSelectAllAcrossPagination] = useState(false);
  const [deselectedJobIds, setDeselectedJobIds] = useState<string[]>([]);

  const getEffectiveSelectedCount = useCallback(() => {
    if (isSelectAllAcrossPagination) {
      return jobsCount - deselectedJobIds.length;
    }
    return selectedJobs.length;
  }, [isSelectAllAcrossPagination, jobsCount, deselectedJobIds.length, selectedJobs.length]);

  const handleJobCheckboxSelect = useCallback(
    (job: JobListItem) => {
      if (isSelectAllAcrossPagination) {
        setDeselectedJobIds(prev => {
          if (prev.includes(job.id)) {
            return prev.filter(id => id !== job.id);
          }
          return [...prev, job.id];
        });
      } else {
        setSelectedJobs(prevSelected => {
          const isAlreadySelected = prevSelected.some(selectedJob => selectedJob.id === job.id);
          if (isAlreadySelected) {
            return prevSelected.filter(selectedJob => selectedJob.id !== job.id);
          }
          return [...prevSelected, job];
        });
      }
    },
    [isSelectAllAcrossPagination]
  );

  const handleSelectAll = useCallback(
    (currentPageJobs: JobListItem[]) => {
      if (currentPageJobs.length === 0) return;

      const currentPageJobIds = currentPageJobs.map(job => job.id);

      if (isSelectAllAcrossPagination) {
        const currentPageEffectivelySelected = currentPageJobIds.filter(jobId => !deselectedJobIds.includes(jobId));
        const hasAnyCurrentPageJobsSelected = currentPageEffectivelySelected.length > 0;

        setDeselectedJobIds(prev => {
          if (hasAnyCurrentPageJobsSelected) {
            return [...new Set([...prev, ...currentPageJobIds])];
          }
          return prev.filter(jobId => !currentPageJobIds.includes(jobId));
        });
      } else {
        const selectedCurrentPageJobs = selectedJobs.filter(job => currentPageJobIds.includes(job.id));
        const hasAnyCurrentPageJobsSelected = selectedCurrentPageJobs.length > 0;

        if (hasAnyCurrentPageJobsSelected) {
          setSelectedJobs(
            prevSelected => prevSelected.filter(selectedJob => !currentPageJobIds.includes(selectedJob.id))
            // eslint-disable-next-line function-paren-newline
          );
        } else {
          setSelectedJobs(prevSelected => {
            const withoutCurrentPage = prevSelected.filter(selectedJob => !currentPageJobIds.includes(selectedJob.id));
            return [...withoutCurrentPage, ...currentPageJobs];
          });
        }
      }
    },
    [isSelectAllAcrossPagination, deselectedJobIds, selectedJobs]
  );

  const handleSelectAllAcrossPagination = useCallback(() => {
    setIsSelectAllAcrossPagination(true);
    setDeselectedJobIds([]);
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelectedJobs([]);
    setIsSelectAllAcrossPagination(false);
    setDeselectedJobIds([]);
  }, []);

  const isJobEffectivelySelected = useCallback(
    (jobId: string) => {
      if (isSelectAllAcrossPagination) {
        return !deselectedJobIds.includes(jobId);
      }
      return selectedJobs.some(job => job.id === jobId);
    },
    [isSelectAllAcrossPagination, deselectedJobIds, selectedJobs]
  );

  const handleSelectStatusGroup = useCallback(
    (_status: JobStatus, statusJobs: JobListItem[]) => {
      if (statusJobs.length === 0) return;

      const statusJobIds = statusJobs.map(job => job.id);

      if (isSelectAllAcrossPagination) {
        const statusJobsEffectivelySelected = statusJobIds.filter(jobId => !deselectedJobIds.includes(jobId));
        const hasAnyStatusJobsSelected = statusJobsEffectivelySelected.length > 0;

        setDeselectedJobIds(prev => {
          if (hasAnyStatusJobsSelected) {
            return [...new Set([...prev, ...statusJobIds])];
          }
          return prev.filter(jobId => !statusJobIds.includes(jobId));
        });
      } else {
        const selectedStatusJobs = selectedJobs.filter(job => statusJobIds.includes(job.id));
        const hasAnyStatusJobsSelected = selectedStatusJobs.length > 0;

        if (hasAnyStatusJobsSelected) {
          setSelectedJobs(prevSelected => prevSelected.filter(selectedJob => !statusJobIds.includes(selectedJob.id)));
        } else {
          setSelectedJobs(prevSelected => {
            const withoutStatusJobs = prevSelected.filter(selectedJob => !statusJobIds.includes(selectedJob.id));
            return [...withoutStatusJobs, ...statusJobs];
          });
        }
      }
    },
    [isSelectAllAcrossPagination, deselectedJobIds, selectedJobs]
  );

  // Cleanup effect when all jobs are deselected
  useEffect(() => {
    if (isSelectAllAcrossPagination && jobsCount > 0 && deselectedJobIds.length === jobsCount) {
      clearAllSelections();
    }
  }, [isSelectAllAcrossPagination, deselectedJobIds.length, jobsCount, clearAllSelections]);

  return {
    selectedJobs,
    isSelectAllAcrossPagination,
    deselectedJobIds,
    getEffectiveSelectedCount,
    handleJobCheckboxSelect,
    handleSelectAll,
    handleSelectAllAcrossPagination,
    clearAllSelections,
    isJobEffectivelySelected,
    handleSelectStatusGroup
  };
};
